import { motion } from "framer-motion";
import { ThumbsUp, Heart, MessageSquare } from "lucide-react";

/**
 * Shared brand logo components — match the user's source logos
 * (SOCIAL GO / SOCIAL PRO / SOCIAL WEEK).
 *
 * - ProductLogo:    stacked icon / SOCIAL / NAME  (data-driven, with optional blur + gradient)
 * - HeartsCascade:  3 stacked hearts for SOCIAL PRO
 * - ChatBubbles:    two overlapping speech bubbles for SOCIAL WEEK
 * - GOLD_GRADIENT:  the Social Week gold gradient string
 *
 * Use anywhere we need to show a brand logo at any scale.
 */

export const GOLD_GRADIENT =
  "linear-gradient(135deg, #d4940a 0%, #e9a820 50%, #d4940a 100%)";

export function HeartsCascade({ color = "#e2e8f0", size = 7 }) {
  return (
    <div className="flex flex-col items-center -space-y-2.5">
      {[0.5, 0.78, 1].map((scale, i) => (
        <Heart
          key={i}
          className={`w-${size} h-${size}`}
          fill={color}
          stroke="none"
          style={{
            transform: `scale(${scale})`,
            opacity: 0.45 + i * 0.18,
          }}
        />
      ))}
    </div>
  );
}

export function ChatBubbles({ color = "#e9a820", accent = "#3a3a3a", size = 9 }) {
  return (
    <div className="relative" style={{ width: `${size * 4}px`, height: `${size * 4}px` }}>
      <MessageSquare
        className="absolute"
        style={{ top: 0, left: size * 0.6, width: `${size * 3}px`, height: `${size * 3}px` }}
        fill={accent}
        stroke="none"
      />
      <MessageSquare
        className="absolute"
        style={{
          top: size * 0.4,
          left: 0,
          width: `${size * 3.4}px`,
          height: `${size * 3.4}px`,
          transform: "scaleX(-1)",
        }}
        fill={color}
        stroke="none"
      />
    </div>
  );
}

const ICONS = {
  GO: (color, sizePx = 48) => (
    <ThumbsUp style={{ width: sizePx, height: sizePx }} fill={color} stroke="none" />
  ),
  PRO: (color) => <HeartsCascade color={color} />,
  WEEK: (color, accent = "#3a3a3a") => <ChatBubbles color={color} accent={accent} />,
};

/**
 * ProductLogo — renders SOCIAL [NAME] in the brand wordmark style.
 *
 * Props:
 *   name        — "GO" | "PRO" | "WEEK"
 *   color       — text colour (used for solid colour); ignored if `gradient` set
 *   gradient    — optional CSS gradient string for the text
 *   blur        — px blur to apply (use for the Social Week tease)
 *   fontSize    — CSS font-size value (e.g. "60px" or "clamp(...)")
 *   iconSize    — px size of the icon (default scales with fontSize)
 *   className / style — passthrough
 *   testId      — data-testid suffix (e.g. testId="social-go" → 'logo-social-go')
 */
export function ProductLogo({
  name,
  color,
  gradient = null,
  blur = 0,
  fontSize = "clamp(34px, 3.6vw, 60px)",
  iconSize,
  iconAccent,
  className = "",
  style = {},
  testId,
}) {
  const iconRender = ICONS[name];
  const computedIconSize = iconSize || (typeof fontSize === "string" ? 48 : 48);

  const textStyle = gradient
    ? {
        background: gradient,
        WebkitBackgroundClip: "text",
        backgroundClip: "text",
        color: "transparent",
      }
    : { color };

  return (
    <div
      className={`select-none pointer-events-none ${className}`}
      style={{ filter: blur ? `blur(${blur}px)` : "none", ...style }}
      data-testid={testId ? `logo-${testId}` : undefined}
    >
      <div className="flex flex-col items-center gap-3">
        {/* Fixed-height icon slot so the SOCIAL word always lands at the
            same baseline regardless of which icon is rendered. */}
        <div
          className="flex items-end justify-center"
          style={{ color, height: computedIconSize, minHeight: computedIconSize }}
        >
          {iconRender
            ? name === "WEEK"
              ? iconRender(color, iconAccent)
              : iconRender(color, computedIconSize)
            : null}
        </div>
        <div
          className="kn-display leading-[0.85] text-center whitespace-nowrap"
          style={{
            ...textStyle,
            fontSize,
            fontWeight: 900,
            letterSpacing: "-0.04em",
          }}
        >
          <div>SOCIAL</div>
          <div>{name}</div>
        </div>
      </div>
    </div>
  );
}

/**
 * AnimatedLogo — wraps ProductLogo with a gentle drift + opacity breathing
 * animation. Use on the Opening Hold and Core Stack scenes.
 */
export function AnimatedLogo({ logoProps, drift = "go", anchor }) {
  const drifts = {
    go: {
      x: [0, 24, -10, 14, 0],
      y: [0, -18, 10, -6, 0],
      opacity: [0.7, 1, 0.8, 0.95, 0.7],
      scale: [1, 1.04, 1, 1.02, 1],
      duration: 18,
      delay: 0,
    },
    pro: {
      x: [0, -22, 12, -10, 0],
      y: [0, 18, -10, 14, 0],
      opacity: [0.6, 0.95, 0.75, 1, 0.6],
      scale: [1, 1.03, 1, 1.05, 1],
      duration: 21,
      delay: 2,
    },
    week: {
      x: [0, 14, -10, 8, 0],
      y: [0, -10, 14, -6, 0],
      opacity: [0.55, 0.85, 0.6, 0.75, 0.55],
      scale: [1, 1.06, 1, 1.04, 1],
      duration: 24,
      delay: 4,
    },
  };
  const cfg = drifts[drift] || drifts.go;

  return (
    <div className="absolute" style={anchor}>
      <motion.div
        animate={{ x: cfg.x, y: cfg.y, opacity: cfg.opacity, scale: cfg.scale }}
        transition={{
          duration: cfg.duration,
          repeat: Infinity,
          ease: "easeInOut",
          delay: cfg.delay,
        }}
      >
        <ProductLogo {...logoProps} />
      </motion.div>
    </div>
  );
}

export default ProductLogo;
